import { ProcessingScreen } from '@components'

import { Flex, ModuleLayout } from '@billy/design-system'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { VatRatesContextProvider } from '@features/vatRates/contexts/vatRatesContext'

import { useSegment } from '../../../../hooks'
import { InvoiceColumnVisibilityFormContextProvider } from './contexts/invoiceColumnVisibilityFormContext'
import { InvoiceEditorActionsContextProvider } from './contexts/invoiceEditorActionsContext'
import { InvoiceEditorContextProvider } from './contexts/invoiceEditorContext'
import { InvoiceEditorDepositModalProvider } from './contexts/invoiceEditorDepositModalContext'
import { InvoiceEditorFormContextProvider } from './contexts/invoiceEditorFormContext'
import { InvoiceEditorFormsProvider } from './contexts/invoiceEditorFormsContext'
import { InvoiceEditorQueryParamsProvider } from './contexts/invoiceEditorQueryParamsContext'
import { InvoiceEditorValuesProvider } from './contexts/invoiceEditorValuesContext'
import { RecurringInvoiceFormContextProvider } from './contexts/recurringInvoiceFormContext'
import { SideActionsModalsContextProvider } from './contexts/sideActionsModalsContext'
import { InvoiceEditorContent } from './elements/InvoiceEditorContent'
import { InvoiceEditorPreviewToggle } from './elements/InvoiceEditorPreviewToggle'
import { InvoiceEditorSideActions } from './elements/InvoiceEditorSideActions'
import { InvoiceEditorTitle } from './elements/InvoiceEditorTitle'
import { InvoiceEditorVatExemptionModals } from './elements/InvoiceEditorVatExemptionModals'
import { useInvoiceEditorBackButtonUrl } from './hooks/useInvoiceEditorBackButtonUrl'
import { useInvoiceEditorVariant } from './hooks/useInvoiceEditorVariant'
import { useQuoteEditorAccess } from './hooks/useQuoteEditorAccess'
import * as Styled from './styles'

export const InvoiceEditor = () => {
  const navigate = useNavigate()
  const { track } = useSegment()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const { backButtonUrl } = useInvoiceEditorBackButtonUrl()
  const {
    canAccessQuoteEditor,
    isLoading: isQuoteEditorAccessLoading,
    redirectUrl: quoteEditorRedirectUrl,
  } = useQuoteEditorAccess()

  const handleBackButtonClick = useCallback(() => {
    track('XXX - Invoicing Core - Invoice Editor - Back arrow button clicked')
  }, [track])

  useEffect(() => {
    if (quoteEditorRedirectUrl) {
      navigate(quoteEditorRedirectUrl, { replace: true })
    }
  }, [navigate, quoteEditorRedirectUrl])

  if (isQuoteEditorAccessLoading || !canAccessQuoteEditor) {
    return <ProcessingScreen />
  }

  return (
    <InvoiceEditorContextProvider invoiceEditorVariant={invoiceEditorVariant}>
      <VatRatesContextProvider>
        <InvoiceEditorFormsProvider>
          <InvoiceEditorQueryParamsProvider>
            <InvoiceEditorDepositModalProvider>
              <InvoiceEditorValuesProvider>
                <InvoiceEditorFormContextProvider>
                  <RecurringInvoiceFormContextProvider>
                    <InvoiceColumnVisibilityFormContextProvider>
                      <SideActionsModalsContextProvider>
                        <InvoiceEditorActionsContextProvider>
                          <InvoiceEditorVatExemptionModals />
                          <ModuleLayout
                            headerContent={
                              <Styled.InvoiceEditorPreviewToggleWrapper>
                                <Flex justifyContent="center" flexGrow={1}>
                                  <InvoiceEditorPreviewToggle />
                                </Flex>
                              </Styled.InvoiceEditorPreviewToggleWrapper>
                            }
                            fullHeight
                            backButtonTo={backButtonUrl}
                            onBackButtonClick={handleBackButtonClick}
                            sideActions={<InvoiceEditorSideActions />}
                            title={<InvoiceEditorTitle />}
                            withBackButton
                          >
                            <InvoiceEditorContent />
                          </ModuleLayout>
                        </InvoiceEditorActionsContextProvider>
                      </SideActionsModalsContextProvider>
                    </InvoiceColumnVisibilityFormContextProvider>
                  </RecurringInvoiceFormContextProvider>
                </InvoiceEditorFormContextProvider>
              </InvoiceEditorValuesProvider>
            </InvoiceEditorDepositModalProvider>
          </InvoiceEditorQueryParamsProvider>
        </InvoiceEditorFormsProvider>
      </VatRatesContextProvider>
    </InvoiceEditorContextProvider>
  )
}
